import React, { useState, useEffect } from "react";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Skeleton, Tooltip, } from "@mui/material";
import { Box } from "@mui/material";
import { stack2 } from "@ui/components/style/style";
import { useGetCombinedMetricsQuery, useGetProjectCostValuesQuery, } from "../../../features/projects/projectsApi";
import { useTheme } from "@mui/material/styles";
import { ScrollCloseTooltip } from "@ui/components/tooltip/ScrollCloseTooltip";
import { formatDutchNumber, formatTooltipWithLineBreaks, } from "@ui/utils/numberFormat";
import { useCompany } from "../../../context/CompanyContext";
const formatGuid = (guid) => {
    if (guid && guid.length === 32 && !guid.includes("-")) {
        return `${guid.slice(0, 8)}-${guid.slice(8, 12)}-${guid.slice(12, 16)}-${guid.slice(16, 20)}-${guid.slice(20)}`;
    }
    return guid;
};
export const ProjectTable = ({ parentProjectNumber, childProjectNumber, unitType, isChildProject, isBelowValuationLevel = false, isOutsideValuationLevel = false, projectId, nodeDepth, }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    const [isLocalLoading, setIsLocalLoading] = useState(false);
    const theme = useTheme();
    const { getSelectedCompanyIds } = useCompany();
    const companyId = getSelectedCompanyIds()[0] || "";
    const { data: combinedMetrics, isLoading, error, isFetching, isUninitialized, } = useGetCombinedMetricsQuery({
        parentProjectNumber,
        childProjectNumber,
        companyId,
        isChildProject,
        nodeDepth: nodeDepth,
    }, {
        skip: !parentProjectNumber ||
            !childProjectNumber ||
            !companyId ||
            nodeDepth === undefined,
        refetchOnMountOrArgChange: true,
    });
    const { data: costValues } = useGetProjectCostValuesQuery({
        projectId: formatGuid(projectId),
        projectNumber: parentProjectNumber,
        childProjectNumber,
        companyId,
        isChildProject,
    }, {
        skip: !projectId || !parentProjectNumber || !childProjectNumber || !companyId,
        refetchOnMountOrArgChange: true,
    });
    // Show local loading when childProjectNumber changes
    useEffect(() => {
        if (childProjectNumber) {
            setIsLocalLoading(true);
        }
    }, [childProjectNumber]);
    // Clear local loading when we have data or when query finishes
    useEffect(() => {
        if (combinedMetrics || (!isLoading && !isFetching)) {
            setIsLocalLoading(false);
        }
    }, [combinedMetrics, isLoading, isFetching]);
    // Derive M1 and M column names from cost values — must be before any early returns
    const costColumnsSorted = React.useMemo(() => {
        if (!costValues)
            return [];
        const unique = Array.from(new Set(costValues
            .map((cv) => cv.columnName)
            .filter((n) => n !== "Gemaakt" && n !== "Total")));
        return unique
            .map((colName) => {
            const cv = costValues.find((c) => c.columnName === colName);
            return {
                columnName: colName,
                displayColumnName: (cv === null || cv === void 0 ? void 0 : cv.displayColumnName) || colName,
                configMonth: (cv === null || cv === void 0 ? void 0 : cv.configMonth) ? new Date(cv.configMonth) : null,
            };
        })
            .filter((c) => c.configMonth !== null)
            .sort((a, b) => b.configMonth.getTime() - a.configMonth.getTime());
    }, [costValues]);
    const localMColumnName = (_a = costColumnsSorted[0]) === null || _a === void 0 ? void 0 : _a.columnName;
    // Fall back to the most recent column when only one month of local data exists
    // (carry-forward: the single available column represents both M and M-1)
    const localM1ColumnName = (_c = (_b = costColumnsSorted[1]) === null || _b === void 0 ? void 0 : _b.columnName) !== null && _c !== void 0 ? _c : (_d = costColumnsSorted[0]) === null || _d === void 0 ? void 0 : _d.columnName;
    const tableSx = {
        width: "100%",
    };
    const NB_TOOLTIP = "Niet beschikbaar vanwege het gekozen waarderingsniveau";
    const NBCell = () => (React.createElement("span", { style: {
            cursor: "help",
            fontSize: "0.65rem",
            fontWeight: 700,
            color: "#9e9e9e",
            letterSpacing: "0.06em",
            lineHeight: 1,
        } }, "NB"));
    // Helper function to get metric source tooltip (DB vs Calculated)
    // gridType: "first" for Totaaloverzicht, "second" for Project To Date Overzicht
    const getMetricSourceTooltip = (metricName, gridType = "first") => {
        const key = metricName.trim().toLowerCase();
        // Determine kosten tooltip based on grid type
        const kostenTooltip = gridType === "first"
            ? "Bron: Datalake\nKolomnaam: estimated_total_costs_ptd"
            : "Bron: Datalake\nKolomnaam: total_costs_ptd";
        // ProjectMarge formula differs between top grid (first) and bottom grid (second)
        const projectMargeTooltip = gridType === "first"
            ? "Van formule: (Aanneemsom - Kosten)" // Top grid: no poc multiplication
            : "Van formule: (Aanneemsom - Kosten) * poc"; // Bottom grid: with poc multiplication
        // ProjectMarge% formula differs between top grid (first) and bottom grid (second)
        const projectMargePercentTooltip = gridType === "first"
            ? "Van formule: (ProjectMarge / Aanneemsom) * 100" // Top grid: projectMarge/Opbrengsten
            : "Van formule: (Projectmarge / omzet) * 100"; // Bottom grid: (Projectmarge / omzet) * 100
        const tooltips = {
            // Direct from Datalake
            omzet: "Van formule: total_costs_ptd + projectmarge",
            opbrengsten: "Bron: Datalake\nKolomnaam: contract_ptd",
            ohw: "Van formule: (Aanneemsom - invoiced_ptd)",
            kosten: kostenTooltip,
            poc: "Van formule: (total_costs_ptd / forecast)",
            gefactureerd: "Bron: Datalake\nKolomnaam: invoiced_ptd",
            // Calculated
            projectmarge: projectMargeTooltip,
            "projectmarge%": projectMargePercentTooltip,
            "forecast margin": "Van formule: GeschatteTotaleKosten * Markup",
            "afgerond%": "Van formule: (TotaleKostenPTD / GeschatteTotaleKostenPTD) * 100",
            // Same text the row showed when it was still labelled "Opbrengsten" —
            // the rename is a label change only, the tooltip stays as it was.
            aanneemsom: "Bron: Datalake\nKolomnaam: contract_ptd",
        };
        // Exact match
        if (tooltips[key])
            return formatTooltipWithLineBreaks(tooltips[key]);
        // Fuzzy matches for common variations
        if (key.includes("opbrengst"))
            return formatTooltipWithLineBreaks(tooltips.opbrengsten);
        if (key.includes("omzet"))
            return formatTooltipWithLineBreaks(tooltips.omzet);
        if (key.includes("ohw"))
            return formatTooltipWithLineBreaks(tooltips.ohw);
        if (key.includes("kosten") || key.includes("cost"))
            return formatTooltipWithLineBreaks(tooltips.kosten);
        if (key.includes("poc"))
            return formatTooltipWithLineBreaks(tooltips.poc);
        if (key.includes("gefactureerd") || key.includes("invoice"))
            return formatTooltipWithLineBreaks(tooltips.gefactureerd);
        if ((key.includes("marg") ||
            key.includes("margin") ||
            key.includes("projectmarg")) &&
            key.includes("%"))
            return formatTooltipWithLineBreaks(tooltips["projectmarge%"]);
        if (key.includes("marg") ||
            key.includes("margin") ||
            key.includes("projectmarg"))
            return formatTooltipWithLineBreaks(tooltips.projectmarge);
        if (key.includes("forecast") && key.includes("margin"))
            return formatTooltipWithLineBreaks(tooltips["forecast margin"]);
        if (key.includes("afgerond"))
            return formatTooltipWithLineBreaks(tooltips["afgerond%"]);
        if (key.includes("aanneemsom"))
            return formatTooltipWithLineBreaks(tooltips.aanneemsom);
        return "";
    };
    // Parses a single numeric value string and returns its abbreviated form.
    // Handles both Dutch format (dot=thousands, comma=decimal: "1.403.033")
    // and English format (comma=thousands, dot=decimal: "1,403,033").
    // Only called on the value segment of a tooltip line.
    const abbreviateValue = (raw) => {
        const trimmed = raw.trim();
        const isNegative = trimmed.startsWith("-");
        const withoutSign = isNegative ? trimmed.substring(1) : trimmed;
        const hasDot = withoutSign.includes(".");
        const hasComma = withoutSign.includes(",");
        let num;
        if (hasDot && hasComma) {
            // Mixed separators: determine format by which comes first
            // e.g. "1,403,033.5" (English) vs "1.403.033,5" (Dutch)
            const firstDot = withoutSign.indexOf(".");
            const firstComma = withoutSign.indexOf(",");
            if (firstComma < firstDot) {
                // English: commas are thousands, dot is decimal → remove commas
                num = parseFloat(withoutSign.replace(/,/g, ""));
            }
            else {
                // Dutch: dots are thousands, comma is decimal → remove dots, comma→dot
                num = parseFloat(withoutSign.replace(/\./g, "").replace(",", "."));
            }
        }
        else if (hasComma !== hasDot) {
            // Exactly one kind of separator. It is a thousands separator only when the final group is
            // 3 digits ("438,163"); 1-2 trailing digits can only be a decimal fraction ("0.80"), which
            // is what amounts look like now that the API sends them with 2 decimals.
            const lastSeparator = Math.max(withoutSign.lastIndexOf("."), withoutSign.lastIndexOf(","));
            const trailingDigits = withoutSign.length - lastSeparator - 1;
            num =
                trailingDigits === 3
                    ? parseFloat(withoutSign.replace(/[.,]/g, ""))
                    : parseFloat(withoutSign.replace(/[.,](?=.*[.,])/g, "").replace(/[.,]/, "."));
        }
        else {
            // Plain integer "15000"
            num = parseFloat(withoutSign);
        }
        if (isNaN(num))
            return raw;
        const sign = isNegative ? "-" : "";
        if (unitType === "M") {
            if (num >= 1000000) {
                const m = num / 1000000;
                const fmt = Math.round(m).toString();
                return `${sign}${fmt}M`;
            }
            else if (num >= 1000) {
                return `${sign}${Math.round(num / 1000)}K`;
            }
        }
        else {
            // K: always divide by 1 000 for consistent units.
            // Values < 1 000 show as decimals (e.g. 592 → 0,592).
            if (num >= 1000) {
                return `${sign}${formatDutchNumber(num / 1000, 0)}`;
            }
            return `${sign}${formatDutchNumber(num / 1000, 0)}`;
        }
        return raw;
    };
    // Helper function to format tooltip text with full or abbreviated numbers.
    // Each line is expected to have the shape "Label: Label: Value".
    // Abbreviation is applied ONLY to the value segment (after the last ": ")
    // so project-number suffixes like "E02.1" or "W07.1.1" are never touched.
    const formatTooltipText = (text) => {
        if (!text)
            return "";
        let rawParts = [];
        if (text.includes("\n\n")) {
            rawParts = text
                .split("\n\n")
                .map((p) => p.trim())
                .filter(Boolean);
        }
        else if (text.includes("\n")) {
            rawParts = text
                .split("\n")
                .map((p) => p.trim())
                .filter(Boolean);
        }
        else if (text.includes("&")) {
            rawParts = text
                .split("&")
                .map((p) => p.trim())
                .filter(Boolean);
        }
        else {
            rawParts = [text.trim()].filter(Boolean);
        }
        if (rawParts.length === 0)
            return text;
        const formattedParts = rawParts.map((part) => {
            if (unitType === "full")
                return part;
            // Only abbreviate the value after the last ": "
            const lastColon = part.lastIndexOf(": ");
            if (lastColon === -1)
                return part;
            const label = part.substring(0, lastColon);
            const value = part.substring(lastColon + 2);
            // Abbreviate each number individually so formula strings like
            // "592 - 460,480 = -459,887" become "0,592 - 460 = -460" in K mode.
            // Operators, parentheses and spacing are preserved as-is.
            // The trailing (?:[.,]\d{1,2})? keeps a 2-decimal fraction attached to its number —
            // without it "1,403,033.50" split into "1,403,033" and "50" and the tail was abbreviated
            // as if it were a separate amount.
            const abbreviated = value.replace(/\d{1,3}(?:[.,]\d{3})*(?:[.,]\d{1,2})?|\d+(?:[.,]\d{1,2})?/g, (numStr) => abbreviateValue(numStr));
            return `${label}: ${abbreviated}`;
        });
        // Return JSX with line breaks if multiple parts
        if (formattedParts.length === 1) {
            return formattedParts[0];
        }
        return (React.createElement(React.Fragment, null, formattedParts.map((part, index) => (React.createElement(React.Fragment, { key: index },
            part,
            index < formattedParts.length - 1 && React.createElement("br", null))))));
    };
    // Helper function to format numbers using Dutch convention with negative formatting
    const formatNumber = (value) => {
        if (value === 0)
            return "-";
        const absValue = Math.abs(value);
        // Format based on unit type
        let formatted;
        if (unitType === "full") {
            formatted = absValue.toLocaleString("nl-NL", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            });
        }
        else if (unitType === "M") {
            // Always show in millions when M is selected
            const millions = absValue / 1000000;
            formatted = formatDutchNumber(millions, 0) + "M";
        }
        else {
            // K unit type - always divide by 1 000 for consistent units.
            formatted = formatDutchNumber(absValue / 1000, 0);
        }
        if (value < 0) {
            return React.createElement("span", { style: { color: "red" } },
                "(",
                formatted,
                ")");
        }
        return formatted;
    };
    const isPercentageMetric = (metricName) => {
        const lower = metricName.toLowerCase();
        return lower.includes("%") || lower.includes("poc");
    };
    // Helper function to format percentage with negative formatting
    // toLocaleString with nl-NL already uses Dutch format (dot for thousands, comma for decimals)
    const formatPercentage = (value) => {
        if (value === 0)
            return "0,00%";
        const absValue = Math.abs(value);
        // Format with Dutch locale which uses comma for decimals and dot for thousands
        const formatted = absValue.toLocaleString("nl-NL", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
        const formattedWithPercent = `${formatted}%`;
        if (value < 0) {
            return React.createElement("span", { style: { color: "red" } },
                "(",
                formattedWithPercent,
                ")");
        }
        return formattedWithPercent;
    };
    // Show loader when loading, fetching, or when query is uninitialized but not skipped
    const shouldShowLoader = isLocalLoading ||
        isLoading ||
        isFetching ||
        (isUninitialized && !(!parentProjectNumber || !childProjectNumber));
    if (shouldShowLoader) {
        return (React.createElement(Box, { sx: Object.assign({ mt: 2 }, stack2) },
            React.createElement(Skeleton, { variant: "text", width: 220, height: 40, sx: { mb: 2 } }),
            React.createElement(TableContainer, null,
                React.createElement(Table, { size: "small", sx: tableSx },
                    React.createElement(TableHead, null,
                        React.createElement(TableRow, null,
                            React.createElement(TableCell, { align: "left", sx: { fontWeight: 600, color: theme.palette.tertiary[30] } },
                                React.createElement(Skeleton, { variant: "text", width: 80 })),
                            React.createElement(TableCell, { align: "left", sx: { fontWeight: 600, color: theme.palette.tertiary[30] } },
                                React.createElement(Skeleton, { variant: "text", width: 60 })),
                            React.createElement(TableCell, { align: "left", sx: { fontWeight: 600, color: theme.palette.tertiary[30] } },
                                React.createElement(Skeleton, { variant: "text", width: 60 })),
                            React.createElement(TableCell, { align: "left", sx: { fontWeight: 600, color: theme.palette.tertiary[30] } },
                                React.createElement(Skeleton, { variant: "text", width: 80 })))),
                    React.createElement(TableBody, null, [...Array(8)].map((_, index) => (React.createElement(TableRow, { key: index },
                        React.createElement(TableCell, { align: "left" },
                            React.createElement(Skeleton, { variant: "text", width: 120 })),
                        React.createElement(TableCell, { align: "left" },
                            React.createElement(Skeleton, { variant: "text", width: 80 })),
                        React.createElement(TableCell, { align: "left" },
                            React.createElement(Skeleton, { variant: "text", width: 80 })),
                        React.createElement(TableCell, { align: "left" },
                            React.createElement(Skeleton, { variant: "text", width: 80 })))))))),
            React.createElement(Skeleton, { variant: "text", width: 240, height: 40, sx: { mt: 3, mb: 2 } }),
            React.createElement(TableContainer, null,
                React.createElement(Table, { size: "small", sx: tableSx },
                    React.createElement(TableHead, null,
                        React.createElement(TableRow, null,
                            React.createElement(TableCell, { align: "left", sx: { fontWeight: 600, color: theme.palette.tertiary[30] } },
                                React.createElement(Skeleton, { variant: "text", width: 80 })),
                            React.createElement(TableCell, { align: "left", sx: { fontWeight: 600, color: theme.palette.tertiary[30] } },
                                React.createElement(Skeleton, { variant: "text", width: 60 })),
                            React.createElement(TableCell, { align: "left", sx: { fontWeight: 600, color: theme.palette.tertiary[30] } },
                                React.createElement(Skeleton, { variant: "text", width: 60 })),
                            React.createElement(TableCell, { align: "left", sx: { fontWeight: 600, color: theme.palette.tertiary[30] } },
                                React.createElement(Skeleton, { variant: "text", width: 60 })),
                            React.createElement(TableCell, { align: "left", sx: { fontWeight: 600, color: theme.palette.tertiary[30] } },
                                React.createElement(Skeleton, { variant: "text", width: 60 })),
                            React.createElement(TableCell, { align: "left", sx: { fontWeight: 600, color: theme.palette.tertiary[30] } },
                                React.createElement(Skeleton, { variant: "text", width: 80 })))),
                    React.createElement(TableBody, null, [...Array(8)].map((_, index) => (React.createElement(TableRow, { key: `second-${index}` },
                        React.createElement(TableCell, { align: "left" },
                            React.createElement(Skeleton, { variant: "text", width: 120 })),
                        React.createElement(TableCell, { align: "left" },
                            React.createElement(Skeleton, { variant: "text", width: 80 })),
                        React.createElement(TableCell, { align: "left" },
                            React.createElement(Skeleton, { variant: "text", width: 80 })),
                        React.createElement(TableCell, { align: "left" },
                            React.createElement(Skeleton, { variant: "text", width: 80 })),
                        React.createElement(TableCell, { align: "left" },
                            React.createElement(Skeleton, { variant: "text", width: 80 })),
                        React.createElement(TableCell, { align: "left" },
                            React.createElement(Skeleton, { variant: "text", width: 80 }))))))))));
    }
    if (error) {
        return (React.createElement(Box, { sx: Object.assign(Object.assign({ mt: 2 }, stack2), { textAlign: "center", p: 2, color: "error.main" }) }, "Fout bij het laden van projectmetrieken"));
    }
    if (!combinedMetrics) {
        return (React.createElement(Box, { sx: Object.assign(Object.assign({ mt: 2 }, stack2), { textAlign: "center", p: 2, color: "text.secondary" }) }, "Geen metriekgegevens beschikbaar"));
    }
    // Builds an enriched tooltip for Kosten cells that combines the backend breakdown
    // with locally-saved cost values (Materiaal / Onderaanneming / Arbeid) and,
    // when the backend provides it, the "without local overrides" figure.
    const buildKostenTooltip = (backendBreakdown, withoutLocalBreakdown, targetColumnName) => {
        const localSection = (() => {
            var _a;
            if (!isChildProject || !costValues || !targetColumnName)
                return null;
            const fmt = (v) => {
                if (v == null)
                    return "-";
                const abs = Math.abs(v);
                let formatted;
                if (unitType === "full") {
                    formatted = formatDutchNumber(abs, 0);
                }
                else {
                    formatted =
                        abs >= 1000
                            ? formatDutchNumber(abs / 1000, 0)
                            : formatDutchNumber(abs, 0);
                }
                return v < 0 ? `(${formatted})` : formatted;
            };
            const get = (row) => {
                var _a, _b;
                return (_b = (_a = costValues.find((cv) => cv.rowName === row && cv.columnName === targetColumnName)) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : null;
            };
            const materiaal = get("Materiaal");
            const onderaanneming = get("Onderaanneming");
            const arbeid = get("Arbeid");
            const hasAny = [materiaal, onderaanneming, arbeid].some((v) => v !== null && v !== 0);
            if (!hasAny)
                return null;
            const displayName = ((_a = costColumnsSorted.find((c) => c.columnName === targetColumnName)) === null || _a === void 0 ? void 0 : _a.displayColumnName) || targetColumnName;
            const total = (materiaal !== null && materiaal !== void 0 ? materiaal : 0) + (onderaanneming !== null && onderaanneming !== void 0 ? onderaanneming : 0) + (arbeid !== null && arbeid !== void 0 ? arbeid : 0);
            return { displayName, materiaal, onderaanneming, arbeid, total, fmt };
        })();
        const hasContent = backendBreakdown || localSection || withoutLocalBreakdown;
        if (!hasContent)
            return "";
        return (React.createElement(Box, { sx: { fontSize: "0.75rem", lineHeight: 1.7 } },
            backendBreakdown && React.createElement(Box, null, formatTooltipText(backendBreakdown)),
            localSection && (React.createElement(Box, { sx: { mt: backendBreakdown ? 0.75 : 0 } },
                React.createElement(Box, { sx: {
                        fontWeight: 700,
                        borderBottom: "1px solid rgba(255,255,255,0.35)",
                        pb: 0.25,
                        mb: 0.5,
                    } },
                    "Lokaal ingevoerd (",
                    localSection.displayName,
                    "):"),
                React.createElement(Box, null,
                    "Materiaal: ",
                    localSection.fmt(localSection.materiaal)),
                React.createElement(Box, null,
                    "Onderaanneming: ",
                    localSection.fmt(localSection.onderaanneming)),
                React.createElement(Box, null,
                    "Arbeid: ",
                    localSection.fmt(localSection.arbeid)),
                React.createElement(Box, { sx: { fontWeight: 600, mt: 0.5 } },
                    "Totaal kosten (lokaal): ",
                    localSection.fmt(localSection.total)))),
            withoutLocalBreakdown && (React.createElement(Box, { sx: { mt: 0.75, opacity: 0.85 } },
                React.createElement(Box, { sx: {
                        fontWeight: 600,
                        borderBottom: "1px solid rgba(255,255,255,0.25)",
                        pb: 0.25,
                        mb: 0.5,
                    } }, "Zonder lokale aanpassing:"),
                React.createElement(Box, null, formatTooltipText(withoutLocalBreakdown))))));
    };
    const getFinancialBreakdownTooltip = (metricName, column) => {
        if (!(combinedMetrics === null || combinedMetrics === void 0 ? void 0 : combinedMetrics.financialBreakdown))
            return "";
        const breakdown = combinedMetrics.financialBreakdown;
        const metricLower = metricName.toLowerCase();
        let tooltipText = "";
        // Check for revenue/income related terms (omzet, aanneemsom, opbrengsten, revenue).
        // "Aanneemsom" is the label the first grid uses for what the backend calls opbrengsten.
        if (metricLower.includes("omzet") ||
            metricLower.includes("revenue") ||
            metricLower.includes("opbrengst") ||
            metricLower.includes("aanneemsom")) {
            if (column === "vc")
                tooltipText = breakdown.breakdownVCRevenue || "";
            if (column === "m1")
                tooltipText = breakdown.breakdownM1Revenue || "";
            if (column === "forecast")
                tooltipText = breakdown.breakdownForecastRevenue || "";
        }
        // Check for cost related terms
        else if (metricLower.includes("kosten") || metricLower.includes("costs")) {
            if (column === "vc")
                tooltipText = breakdown.breakdownVCCosts || "";
            if (column === "m1")
                return buildKostenTooltip(breakdown.breakdownM1Costs || "", breakdown.breakdownM1CostsWithoutLocal, localM1ColumnName !== null && localM1ColumnName !== void 0 ? localM1ColumnName : null);
            if (column === "forecast")
                return buildKostenTooltip(breakdown.breakdownForecastCosts || "", breakdown.breakdownForecastCostsWithoutLocal, localMColumnName !== null && localMColumnName !== void 0 ? localMColumnName : null);
        }
        // Check for margin percentage (margin%)
        else if ((metricLower.includes("marg") || metricLower.includes("margin")) &&
            metricLower.includes("%")) {
            if (column === "vc")
                tooltipText = breakdown.breakdownVCMarginPercent || "";
            if (column === "m1")
                tooltipText = breakdown.breakdownM1MarginPercent || "";
            if (column === "forecast")
                tooltipText = breakdown.breakdownForecastMarginPercent || "";
        }
        // Check for margin related terms (margin, marg) but exclude percentage rows (margin%)
        else if ((metricLower.includes("marg") || metricLower.includes("margin")) &&
            !metricLower.includes("%")) {
            if (column === "vc")
                tooltipText = breakdown.breakdownVCMargin || "";
            if (column === "m1")
                tooltipText = breakdown.breakdownM1Margin || "";
            if (column === "forecast")
                tooltipText = breakdown.breakdownForecastMargin || "";
        }
        // Convert numbers in tooltip to Dutch format and add line breaks
        return formatTooltipText(tooltipText);
    };
    // Extract labels with fallback values
    const vcLabel = (_e = combinedMetrics.vcLabel) !== null && _e !== void 0 ? _e : "Voorcalculatie";
    const m1Label = (_f = combinedMetrics.m1Label) !== null && _f !== void 0 ? _f : "M1";
    const mLabel = (_g = combinedMetrics.mLabel) !== null && _g !== void 0 ? _g : "M";
    const ptdLabel = (_h = combinedMetrics.ptdLabel) !== null && _h !== void 0 ? _h : "PTD";
    const previousMonth = (_j = combinedMetrics.previousMonth) !== null && _j !== void 0 ? _j : "config month -1";
    return (React.createElement(Box, { sx: Object.assign({ mt: 2 }, stack2) },
        React.createElement(Typography, { variant: "h5", sx: { fontWeight: 500 } }, "Totaaloverzicht"),
        React.createElement(TableContainer, null,
            React.createElement(Table, { size: "small", sx: tableSx },
                React.createElement(TableHead, null,
                    React.createElement(TableRow, null,
                        React.createElement(TableCell, { align: "left", sx: { fontWeight: 500, color: theme.palette.tertiary[30] } }, "Metriek"),
                        React.createElement(TableCell, { align: "left", sx: { fontWeight: 500, color: theme.palette.tertiary[30] } }, vcLabel),
                        React.createElement(TableCell, { align: "left", sx: { fontWeight: 500, color: theme.palette.tertiary[30] } }, `Forecast (${previousMonth})`),
                        React.createElement(TableCell, { align: "left", sx: { fontWeight: 500, color: theme.palette.tertiary[30] } },
                            React.createElement(Box, null, `Forecast (${mLabel})`)))),
                React.createElement(TableBody, null, [...combinedMetrics.financials]
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .map((metric, index) => {
                    const metricSourceTooltip = getMetricSourceTooltip(metric.metric, "first");
                    const financialMetricLower = metric.metric.toLowerCase();
                    const isNBApplicableFinancialMetric = financialMetricLower.includes("kosten") ||
                        financialMetricLower.includes("cost") ||
                        financialMetricLower.includes("marg");
                    const isKostenRestricted = isBelowValuationLevel && isNBApplicableFinancialMetric;
                    return (React.createElement(TableRow, { key: index },
                        React.createElement(TableCell, { align: "left" },
                            React.createElement(ScrollCloseTooltip, { title: metricSourceTooltip || "", arrow: true, placement: "top", disableHoverListener: !metricSourceTooltip },
                                React.createElement(Box, { component: "span", sx: {
                                        cursor: metricSourceTooltip ? "help" : "default",
                                    } }, metric.metric))),
                        React.createElement(ScrollCloseTooltip, { title: isKostenRestricted
                                ? NB_TOOLTIP
                                : getFinancialBreakdownTooltip(metric.metric, "vc"), arrow: true, placement: "left-start", disableHoverListener: isKostenRestricted
                                ? false
                                : !getFinancialBreakdownTooltip(metric.metric, "vc") },
                            React.createElement(TableCell, { align: "left", sx: { cursor: "help" } }, isKostenRestricted ? (React.createElement(NBCell, null)) : isPercentageMetric(metric.metric) ? (formatPercentage(metric.vc)) : (formatNumber(metric.vc)))),
                        React.createElement(ScrollCloseTooltip, { title: isKostenRestricted
                                ? NB_TOOLTIP
                                : getFinancialBreakdownTooltip(metric.metric, "m1"), arrow: true, placement: "left-start", disableHoverListener: isKostenRestricted
                                ? false
                                : !getFinancialBreakdownTooltip(metric.metric, "m1") },
                            React.createElement(TableCell, { align: "left", sx: { cursor: "help" } }, isKostenRestricted ? (React.createElement(NBCell, null)) : isPercentageMetric(metric.metric) ? (formatPercentage(metric.m1)) : (formatNumber(metric.m1)))),
                        React.createElement(ScrollCloseTooltip, { title: isKostenRestricted
                                ? NB_TOOLTIP
                                : getFinancialBreakdownTooltip(metric.metric, "forecast"), arrow: true, placement: "left-start", disableHoverListener: isKostenRestricted
                                ? false
                                : !getFinancialBreakdownTooltip(metric.metric, "forecast") },
                            React.createElement(TableCell, { align: "left", sx: { cursor: "help" } }, isKostenRestricted ? (React.createElement(NBCell, null)) : isPercentageMetric(metric.metric) ? (formatPercentage(metric.forecast)) : (formatNumber(metric.forecast))))));
                })))),
        React.createElement(Box, { sx: { display: "flex", alignItems: "center" } },
            React.createElement(Typography, { variant: "h5", sx: { fontWeight: 500 } }, "Project To Date Overzicht"),
            isOutsideValuationLevel && (React.createElement(Tooltip, { title: "Deze sectie en tabel is uitgeschakeld omdat het geselecteerde project zich niet op het ingestelde waarderingsniveau bevindt. Sommige waarden zijn niet beschikbaar of geven niet de juiste weergave. Navigeer naar een project op het waarderingsniveau om de gegevens te bekijken.", arrow: true, placement: "top" },
                React.createElement(Box, { component: "span", sx: {
                        display: "inline-flex",
                        alignItems: "center",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        px: 1,
                        py: 0.25,
                        borderRadius: "24px",
                        backgroundColor: "#FBE67E66",
                        color: "#776304",
                        cursor: "help",
                        ml: "auto",
                    } }, "Niet beschikbaar op dit niveau")))),
        React.createElement(Box, null,
            React.createElement(TableContainer, null,
                React.createElement(Table, { size: "small", sx: tableSx },
                    React.createElement(TableHead, null,
                        React.createElement(TableRow, null,
                            React.createElement(TableCell, { align: "left", sx: { fontWeight: 500, color: theme.palette.tertiary[30] } }),
                            React.createElement(TableCell, { align: "left", sx: { fontWeight: 500, color: theme.palette.tertiary[30] } }, m1Label),
                            React.createElement(TableCell, { align: "left", sx: { fontWeight: 500, color: theme.palette.tertiary[30] } }, mLabel),
                            React.createElement(TableCell, { align: "left", sx: { fontWeight: 500, color: theme.palette.tertiary[30] } }, ptdLabel),
                            React.createElement(TableCell, { align: "left", sx: { fontWeight: 500, color: theme.palette.tertiary[30] } }, "Te gaan"),
                            React.createElement(TableCell, { align: "left", sx: { fontWeight: 500, color: theme.palette.tertiary[30] } }, "Forecast"))),
                    React.createElement(TableBody, null, [...combinedMetrics.additionalMetrics]
                        .sort((a, b) => a.sortOrder - b.sortOrder)
                        .map((metric, index) => {
                        const metricSourceTooltip = getMetricSourceTooltip(metric.metric, "second");
                        const metricLower = metric.metric.toLowerCase();
                        const breakdown = combinedMetrics.additionalBreakdown;
                        // Determine which breakdown to use based on metric name
                        let breakdownM1 = "", breakdownM = "", breakdownPTD = "", breakdownTogan = "", breakdownForecast = "";
                        if (breakdown) {
                            // Check for cost related terms
                            if (metricLower.includes("kosten") ||
                                metricLower.includes("costs")) {
                                breakdownM1 = breakdown.breakdown_Kosten_M1 || "";
                                breakdownM = breakdown.breakdown_Kosten_M || "";
                                breakdownPTD = breakdown.breakdown_Kosten_PTD || "";
                                breakdownTogan = breakdown.breakdown_Kosten_Togan || "";
                                breakdownForecast =
                                    breakdown.breakdown_Kosten_Forecast || "";
                            }
                            // Check for revenue/income related terms (omzet, opbrengsten, revenue)
                            else if (metricLower.includes("omzet") ||
                                metricLower.includes("revenue") ||
                                metricLower.includes("opbrengst")) {
                                breakdownM1 = breakdown.breakdown_Omzet_M1 || "";
                                breakdownM = breakdown.breakdown_Omzet_M || "";
                                breakdownPTD = breakdown.breakdown_Omzet_PTD || "";
                                breakdownTogan = breakdown.breakdown_Omzet_Togan || "";
                                breakdownForecast =
                                    breakdown.breakdown_Omzet_Forecast || "";
                            }
                            // Check for margin percentage (margin% or Projectmarge %)
                            else if ((metricLower.includes("marg") ||
                                metricLower.includes("margin") ||
                                metricLower.includes("projectmarge")) &&
                                (metricLower.includes("%") ||
                                    metricLower.includes("percent"))) {
                                breakdownM1 = breakdown.breakdown_MargPercent_M1 || "";
                                breakdownM = breakdown.breakdown_MargPercent_M || "";
                                breakdownPTD = breakdown.breakdown_MargPercent_PTD || "";
                                breakdownTogan =
                                    breakdown.breakdown_MargPercent_Togan || "";
                                breakdownForecast =
                                    breakdown.breakdown_MargPercent_Forecast || "";
                            }
                            // Check for margin related terms (margin, marg) but exclude percentage rows (margin%)
                            else if ((metricLower.includes("marg") ||
                                metricLower.includes("margin")) &&
                                !metricLower.includes("%")) {
                                breakdownM1 = breakdown.breakdown_Marg_M1 || "";
                                breakdownM = breakdown.breakdown_Marg_M || "";
                                breakdownPTD = breakdown.breakdown_Marg_PTD || "";
                                breakdownTogan = breakdown.breakdown_Marg_Togan || "";
                                breakdownForecast =
                                    breakdown.breakdown_Marg_Forecast || "";
                            }
                            // Check for POC related terms
                            else if (metricLower.includes("poc")) {
                                breakdownM1 = breakdown.breakdown_POC_M1 || "";
                                breakdownM = breakdown.breakdown_POC_M || "";
                                breakdownPTD = breakdown.breakdown_POC_PTD || "";
                                breakdownTogan = breakdown.breakdown_POC_Togan || "";
                                breakdownForecast =
                                    breakdown.breakdown_POC_Forecast || "";
                            }
                            // Check for OHW related terms
                            else if (metricLower.includes("ohw")) {
                                breakdownM1 = breakdown.breakdown_OHW_M1 || "";
                                breakdownM = breakdown.breakdown_OHW_M || "";
                                breakdownPTD = breakdown.breakdown_OHW_PTD || "";
                                breakdownTogan = breakdown.breakdown_OHW_Togan || "";
                                breakdownForecast =
                                    breakdown.breakdown_OHW_Forecast || "";
                            }
                            // Check for Invoice/Gefactureerd related terms
                            else if (metricLower.includes("gefactureerd") ||
                                metricLower.includes("invoice")) {
                                breakdownM1 = breakdown.breakdown_Invoice_M1 || "";
                                breakdownM = breakdown.breakdown_Invoice_M || "";
                                breakdownPTD = breakdown.breakdown_Invoice_PTD || "";
                                breakdownTogan = breakdown.breakdown_Invoice_Togan || "";
                                breakdownForecast =
                                    breakdown.breakdown_Invoice_Forecast || "";
                            }
                        }
                        const isKosten = metricLower.includes("kosten") ||
                            metricLower.includes("costs");
                        // NB applies to Kosten, POC, Project Margin and Project Margin %
                        const isNBApplicableAdditionalMetric = isKosten ||
                            metricLower.includes("poc") ||
                            metricLower.includes("marg");
                        const isKostenRestrictedAdditional = isBelowValuationLevel && isNBApplicableAdditionalMetric;
                        // Rows are greyed when not at the valuation level (both above and below)
                        const isRowGreyedOut = isOutsideValuationLevel;
                        // For Kosten, enrich M-1, M, and Forecast tooltips with locally-saved cost values
                        const tooltipM1Node = isKosten
                            ? buildKostenTooltip(breakdownM1, breakdown === null || breakdown === void 0 ? void 0 : breakdown.breakdown_Kosten_M1_WithoutLocal, localM1ColumnName !== null && localM1ColumnName !== void 0 ? localM1ColumnName : null)
                            : formatTooltipText(breakdownM1);
                        const tooltipMNode = isKosten
                            ? buildKostenTooltip(breakdownM, breakdown === null || breakdown === void 0 ? void 0 : breakdown.breakdown_Kosten_M_WithoutLocal, localMColumnName !== null && localMColumnName !== void 0 ? localMColumnName : null)
                            : formatTooltipText(breakdownM);
                        const tooltipForecastNode = isKosten
                            ? buildKostenTooltip(breakdownForecast, breakdown === null || breakdown === void 0 ? void 0 : breakdown.breakdown_Kosten_Forecast_WithoutLocal, localMColumnName !== null && localMColumnName !== void 0 ? localMColumnName : null)
                            : formatTooltipText(breakdownForecast);
                        const hasTooltipM1 = isKosten
                            ? !!(breakdownM1 || localM1ColumnName)
                            : !!breakdownM1;
                        const hasTooltipM = isKosten
                            ? !!(breakdownM || localMColumnName)
                            : !!breakdownM;
                        const hasTooltipForecast = isKosten
                            ? !!(breakdownForecast || localMColumnName)
                            : !!breakdownForecast;
                        return (React.createElement(TableRow, { key: index, sx: isRowGreyedOut
                                ? { opacity: 0.4, filter: "grayscale(1)" }
                                : undefined },
                            React.createElement(TableCell, { align: "left" },
                                React.createElement(ScrollCloseTooltip, { title: metricSourceTooltip || "", arrow: true, placement: "top", disableHoverListener: !metricSourceTooltip },
                                    React.createElement(Box, { component: "span", sx: {
                                            cursor: metricSourceTooltip ? "help" : "default",
                                        } }, metric.metric))),
                            React.createElement(ScrollCloseTooltip, { title: isKostenRestrictedAdditional
                                    ? NB_TOOLTIP
                                    : tooltipM1Node, arrow: true, placement: "left-start", disableHoverListener: isKostenRestrictedAdditional ? false : !hasTooltipM1 },
                                React.createElement(TableCell, { align: "left", sx: {
                                        cursor: hasTooltipM1 || isKostenRestrictedAdditional
                                            ? "help"
                                            : "default",
                                    } }, isKostenRestrictedAdditional ? (React.createElement(NBCell, null)) : isPercentageMetric(metric.metric) ? (formatPercentage(metric.m1)) : metric.m1 === 0 &&
                                    combinedMetrics.marginM1Display === false &&
                                    metricLower.includes("marg") &&
                                    !metricLower.includes("%") ? ("0") : (formatNumber(metric.m1)))),
                            React.createElement(ScrollCloseTooltip, { title: isKostenRestrictedAdditional
                                    ? NB_TOOLTIP
                                    : tooltipMNode, arrow: true, placement: "left-start", disableHoverListener: isKostenRestrictedAdditional ? false : !hasTooltipM },
                                React.createElement(TableCell, { align: "left", sx: {
                                        cursor: hasTooltipM || isKostenRestrictedAdditional
                                            ? "help"
                                            : "default",
                                    } }, isKostenRestrictedAdditional ? (React.createElement(NBCell, null)) : isPercentageMetric(metric.metric) ? (formatPercentage(metric.m)) : (formatNumber(metric.m)))),
                            React.createElement(ScrollCloseTooltip, { title: isKostenRestrictedAdditional
                                    ? NB_TOOLTIP
                                    : formatTooltipText(breakdownPTD), arrow: true, placement: "left-start", disableHoverListener: isKostenRestrictedAdditional ? false : !breakdownPTD },
                                React.createElement(TableCell, { align: "left", sx: {
                                        cursor: breakdownPTD || isKostenRestrictedAdditional
                                            ? "help"
                                            : "default",
                                    } }, isKostenRestrictedAdditional ? (React.createElement(NBCell, null)) : isPercentageMetric(metric.metric) ? (formatPercentage(metric.ptd)) : (formatNumber(metric.ptd)))),
                            React.createElement(ScrollCloseTooltip, { title: isKostenRestrictedAdditional
                                    ? NB_TOOLTIP
                                    : formatTooltipText(breakdownTogan), arrow: true, placement: "left-start", disableHoverListener: isKostenRestrictedAdditional ? false : !breakdownTogan },
                                React.createElement(TableCell, { align: "left", sx: {
                                        cursor: breakdownTogan || isKostenRestrictedAdditional
                                            ? "help"
                                            : "default",
                                    } }, isKostenRestrictedAdditional ? (React.createElement(NBCell, null)) : isPercentageMetric(metric.metric) ? (formatPercentage(metric.togan)) : (formatNumber(metric.togan)))),
                            React.createElement(ScrollCloseTooltip, { title: isKostenRestrictedAdditional
                                    ? NB_TOOLTIP
                                    : tooltipForecastNode, arrow: true, placement: "left-start", disableHoverListener: isKostenRestrictedAdditional
                                    ? false
                                    : !hasTooltipForecast },
                                React.createElement(TableCell, { align: "left", sx: {
                                        cursor: hasTooltipForecast || isKostenRestrictedAdditional
                                            ? "help"
                                            : "default",
                                    } }, isKostenRestrictedAdditional ? (React.createElement(NBCell, null)) : isPercentageMetric(metric.metric) ? (formatPercentage(metric.forecast)) : (formatNumber(metric.forecast))))));
                    })))))));
};
